import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { useSelector } from "react-redux";

import instance from "../../../api/instance";
import parseNumber from "../../../utils/parse-number";
import "react-datepicker/dist/react-datepicker.css";
import { RootState } from "../../../redux/store";
import { useSyncGlobalPageLoader } from "../../../contexts/global-page-loader-context";

interface UserWinLossRow {
  userName: string;
  casino: number;
  sport: number;
  thirdParty: number;
}

interface UserWinLossApiRow {
  id?: string;
  user_id?: string;
  username?: string;
  user_name?: string;
  casino?: number | string;
  casino_win_loss?: number | string;
  casino_balance?: number | string;
  sport?: number | string;
  sports?: number | string;
  sport_win_loss?: number | string;
  sports_win_loss?: number | string;
  sports_balance?: number | string;
  thirdParty?: number | string;
  third_party?: number | string;
  third_party_win_loss?: number | string;
  casino_pnl?: number | string;
  sports_pnl?: number | string;
  total_pnl?: number | string;
}

interface IClientOption {
  user_id: string;
  user_name: string;
  display_name: string;
}

const formatAmount = (value: number) =>
  value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getDefaultDateRange = (): [Date, Date] => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 10);
  return [start, end];
};

const CLIENT_SEARCH_MIN_LENGTH = 2;

const UserWinLossPage = () => {
  const loggedInUserId = useSelector((state: RootState) => state.user.userData?.user_id ?? "");
  const [clientSearchText, setClientSearchText] = useState("");
  const [clientOptions, setClientOptions] = useState<IClientOption[]>([]);
  const [selectedClient, setSelectedClient] = useState<IClientOption | null>(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => getDefaultDateRange());
  const [rows, setRows] = useState<UserWinLossRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  useSyncGlobalPageLoader(loading);
  const [startDate, endDate] = dateRange;
  const clientWrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clientSearchRequestIdRef = useRef(0);

  const query = searchText.trim().toLowerCase();

  const filteredRows = useMemo(() => {
    if (!query) return rows;
    return rows.filter((row) => row.userName.toLowerCase().includes(query));
  }, [rows, query]);

  const totals = useMemo(
    () =>
      filteredRows.reduce(
        (acc, row) => {
          acc.casino += row.casino;
          acc.sport += row.sport;
          acc.thirdParty += row.thirdParty;
          acc.profitLoss += row.casino + row.sport + row.thirdParty;
          return acc;
        },
        { casino: 0, sport: 0, thirdParty: 0, profitLoss: 0 }
      ),
    [filteredRows]
  );

  const fetchClients = useCallback(
    async (queryValue: string) => {
      if (!loggedInUserId) {
        return [] as IClientOption[];
      }
      const requestId = ++clientSearchRequestIdRef.current;
      try {
        const { data } = await instance.get("admin/downline-members", {
          params: {
            adminId: loggedInUserId,
            memberType: "admin",
            scope: "recursive",
            search: queryValue,
          },
        });
        const rawList: any[] = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.data?.rows)
            ? data.data.rows
            : Array.isArray(data?.data?.members)
              ? data.data.members
              : [];
        const list: IClientOption[] = rawList
          .map((item) => ({
            user_id: item?.user_id ?? item?.admin_id ?? item?.id ?? "",
            user_name: item?.user_name ?? item?.username ?? "",
            display_name:
              item?.display_name ?? item?.displayName ?? item?.user_name ?? item?.username ?? "",
          }))
          .filter((item) => item.user_id);
        if (requestId !== clientSearchRequestIdRef.current) return [] as IClientOption[];
        return list;
      } catch {
        if (requestId !== clientSearchRequestIdRef.current) return [] as IClientOption[];
        return [] as IClientOption[];
      }
    },
    [loggedInUserId]
  );

  const handleClientSearchChange = (value: string) => {
    setClientSearchText(value);
    if (selectedClient) {
      setSelectedClient(null);
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const q = value.toLowerCase().trim();
      if (q.length < CLIENT_SEARCH_MIN_LENGTH) {
        setClientOptions([]);
        setShowClientDropdown(true);
        return;
      }
      const source = await fetchClients(q);
      setClientOptions(source);
      setShowClientDropdown(true);
    }, 200);
  };

  const handleSelectClient = (client: IClientOption) => {
    setSelectedClient(client);
    setClientSearchText(client.display_name || client.user_name);
    setShowClientDropdown(false);
  };

  const handleLoad = async (e: FormEvent) => {
    e.preventDefault();
    const adminId = selectedClient?.user_id || loggedInUserId;
    if (!adminId) return;

    setLoading(true);
    try {
      const params: Record<string, string | number> = { adminId };
      if (startDate) {
        const from = new Date(startDate);
        from.setHours(0, 0, 0, 0);
        params.startDate = Math.floor(from.getTime() / 1000);
      }
      if (endDate) {
        const to = new Date(endDate);
        to.setHours(23, 59, 59, 999);
        params.endDate = Math.floor(to.getTime() / 1000);
      }
      const { data } = await instance.get("admin/downline-users-win-loss-by-admin", { params });
      const list: UserWinLossApiRow[] = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.rows)
          ? data.data.rows
          : Array.isArray(data?.data?.list)
            ? data.data.list
            : Array.isArray(data?.data?.members)
              ? data.data.members
              : Array.isArray(data?.data?.userData)
                ? data.data.userData
              : [];
      const mappedRows: UserWinLossRow[] = list.map((item) => ({
        userName: item.user_name ?? item.username ?? "-",
        casino: Number(parseNumber({ value: item.casino_pnl ?? item.casino_win_loss ?? item.casino ?? item.casino_balance ?? 0, type: "float" })),
        sport: Number(parseNumber({ value: item.sports_pnl ?? item.sports_win_loss ?? item.sport_win_loss ?? item.sports ?? item.sport ?? item.sports_balance ?? 0, type: "float" })),
        thirdParty: Number(
          parseNumber({
            value:
              item.third_party_win_loss ??
              item.third_party ??
              item.thirdParty ??
              (item.total_pnl !== undefined
                ? Number(item.total_pnl ?? 0) -
                  Number(item.casino_pnl ?? item.casino_win_loss ?? item.casino ?? item.casino_balance ?? 0) -
                  Number(item.sports_pnl ?? item.sports_win_loss ?? item.sport_win_loss ?? item.sports ?? item.sport ?? item.sports_balance ?? 0)
                : 0),
            type: "float",
          })
        ),
      }));
      setRows(mappedRows);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setClientSearchText("");
    setClientOptions([]);
    setSelectedClient(null);
    setShowClientDropdown(false);
    setDateRange(getDefaultDateRange());
    setSearchText("");
    setRows([]);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (clientWrapperRef.current && !clientWrapperRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="listing-grid report-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">User Win Loss</h4>
            <div className="page-title-right" />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="report-form mb-3">
                <form method="post" className="ajaxFormSubmit" onSubmit={handleLoad}>
                  <div className="row row5">
                    <div className="col-2">
                      <div
                        className="form-group user-lock-search"
                        style={{ position: "relative" }}
                        ref={clientWrapperRef}
                      >
                        <label htmlFor="clientName">Search By Client Name</label>
                        <input
                          id="clientName"
                          type="text"
                          autoComplete="off"
                          spellCheck={false}
                          placeholder="Select option"
                          className="form-control"
                          value={clientSearchText}
                          onChange={(e) => handleClientSearchChange(e.target.value)}
                          onFocus={() => {
                            setClientOptions([]);
                            clientSearchRequestIdRef.current += 1;
                            setShowClientDropdown(true);
                          }}
                        />
                        {showClientDropdown && (
                          <ul
                            className="list-group"
                            style={{
                              position: "absolute",
                              zIndex: 1000,
                              width: "100%",
                              maxHeight: "300px",
                              overflowY: "auto",
                              boxShadow: "0 2px 8px rgba(0,0,0,.15)",
                            }}
                          >
                            {clientOptions.length === 0 ? (
                              <li className="list-group-item">
                                {clientSearchText.trim()
                                  ? "No elements found. Consider changing the search query."
                                  : "List is empty."}
                              </li>
                            ) : (
                              clientOptions.map((client) => (
                                <li key={client.user_id} className="list-group-item list-group-item-action">
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-left w-100"
                                    style={{ textDecoration: "none", color: "inherit" }}
                                    onClick={() => handleSelectClient(client)}
                                  >
                                    {client.display_name || client.user_name}
                                  </button>
                                </li>
                              ))
                            )}
                          </ul>
                        )}
                      </div>
                    </div>

                    <div className="col-3">
                      <label htmlFor="dateRange">Select Date Range</label>
                      <div className="mb-3 mx-datepicker mx-datepicker-range">
                        <DatePicker
                          id="dateRange"
                          selectsRange
                          startDate={startDate}
                          endDate={endDate}
                          onChange={(update) => setDateRange(update as [Date | null, Date | null])}
                          isClearable
                          dateFormat="dd/MM/yyyy"
                          className="mx-input"
                          wrapperClassName="d-block w-100"
                        />
                      </div>
                    </div>

                    <div className="col-3">
                      <div style={{ width: "100%", minHeight: "24px" }} aria-hidden="true" />
                      <button type="submit" id="loaddata" className="btn btn-primary">
                        {loading ? "Loading..." : "Load"}
                      </button>{" "}
                      <button type="button" id="reset" className="btn btn-light" onClick={handleReset}>
                        Reset
                      </button>{" "}
                      <div className="d-inline-block">
                        <button type="button" className="btn btn-success" aria-label="Export Excel">
                          <i className="fas fa-file-excel" />
                        </button>
                      </div>{" "}
                      <button type="button" className="btn btn-danger" aria-label="Export PDF">
                        <i className="fas fa-file-pdf" />
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-12">
                  <div id="tickets-table_filter" className="dataTables_filter text-md-right">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="d-inline-flex align-items-center">
                      Search:
                      <input
                        type="search"
                        placeholder="Search..."
                        className="form-control form-control-sm ml-2 form-control"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="table-responsive mb-0">
                <table
                  id="plTbl"
                  role="table"
                  aria-busy="false"
                  aria-colcount={6}
                  className="table b-table table no-footer table-hover table-bordered"
                >
                  <thead>
                    <tr role="row">
                      <th role="columnheader" scope="col" aria-colindex={1}>
                        <div>No</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex={2}>
                        <div>User Name</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex={3} className="text-right">
                        <div>Casino</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex={4} className="text-right">
                        <div>Sport</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex={5} className="text-right">
                        <div>Third Party</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex={6} className="text-right">
                        <div>Profit/Loss</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 ? (
                      <tr className="b-table-empty-row">
                        <td colSpan={6}>
                          <div role="alert" aria-live="polite">
                            <p className="text-center mb-0">{loading ? "Loading..." : "No data available in table"}</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredRows.map((row, idx) => {
                        const profitLoss = row.casino + row.sport + row.thirdParty;
                        return (
                          <tr key={row.userName} role="row">
                            <td aria-colindex={1}>{idx + 1}</td>
                            <td aria-colindex={2}>{row.userName}</td>
                            <td aria-colindex={3} className="text-right">
                              <p className="mb-0">{formatAmount(row.casino)}</p>
                            </td>
                            <td aria-colindex={4} className="text-right">
                              <p className="mb-0">{formatAmount(row.sport)}</p>
                            </td>
                            <td aria-colindex={5} className="text-right">
                              <p className="mb-0">{formatAmount(row.thirdParty)}</p>
                            </td>
                            <td aria-colindex={6} className="text-right">
                              <p className="mb-0">{formatAmount(profitLoss)}</p>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  <tfoot>
                    <tr role="row">
                      <th scope="col" aria-colindex={1}>
                        <span />
                      </th>
                      <th scope="col" aria-colindex={2}>
                        <span />
                      </th>
                      <th scope="col" aria-colindex={3} className="text-right">
                        <span>{formatAmount(totals.casino)}</span>
                      </th>
                      <th scope="col" aria-colindex={4} className="text-right">
                        <span>{formatAmount(totals.sport)}</span>
                      </th>
                      <th scope="col" aria-colindex={5} className="text-right">
                        <span>{formatAmount(totals.thirdParty)}</span>
                      </th>
                      <th scope="col" aria-colindex={6} className="text-right">
                        <span>{formatAmount(totals.profitLoss)}</span>
                      </th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserWinLossPage;
